import { createSelector } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import {
  CheckoutAppSelector,
  postTag,
  updateTag,
  TagsState,
  useAppDispatch,
  useApplePayPayment,
  useAppSelector,
  useExperiment,
  usePreferredPaymentMethod,
  useSavedCards,
  useSelectedPaymentMethod,
} from '../'
import { ConsumerTag, PaymentMethodType } from '../../model'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { consumerTagsService } from '@afterpaytouch/portal-api/consumer/tags'

export const tagsSelector: CheckoutAppSelector<TagsState> = (state) => state.tags
export const activeTags: CheckoutAppSelector<string[]> = createSelector(tagsSelector, ({ active }) => active)
export const useActiveTags = (): string[] => useAppSelector(activeTags)
export const useConsumerTag = (): ConsumerTag => useAppSelector(createSelector(tagsSelector, (state) => state.consumerTag))

type SaveTag = (newTag: string, value?: string) => void

export const useTags = (): [string[], SaveTag] => {
  const currentTags = useActiveTags()
  const dispatch = useAppDispatch()

  const saveTag = useCallback(
    (newTag: string, value?: string): void => {
      const saveToTag = async (tag: string, value?: string): Promise<void> => {
        try {
          const isTagExisting = currentTags.filter((t) => t === tag).length > 0
          const tagUpdateFn = isTagExisting ? updateTag : postTag
          await dispatch(tagUpdateFn({ name: tag, data: value }))
        } catch (e) {}
      }
      saveToTag(newTag, value)
    },
    [dispatch, currentTags]
  )

  return [currentTags, saveTag]
}

export const useShouldShowApplePayBanner = (): [boolean, () => void] => {
  const [tags, saveTag] = useTags()
  const applePayEnabled = useApplePayPayment()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const appleBannerKey = 'applepay-banner'
  const seenApplePayBanner = tags.includes(appleBannerKey)
  const saveToTags = useCallback((): void => saveTag(appleBannerKey), [appleBannerKey, saveTag])

  return [applePayEnabled && preferredPaymentMethod !== PaymentMethodType.APPLE_PAY && !seenApplePayBanner, saveToTags]
}

export const useHasSeenDonation = (donationCampaign?: string): { hasSeenDonation: boolean; saveToTags: () => void } => {
  const [tags, saveTag] = useTags()
  const isValid = donationCampaign !== undefined && donationCampaign.trim().length > 0
  const donationCampaignKey = isValid ? donationCampaign.toLowerCase() : ''
  const seenDonation = tags.includes(donationCampaignKey)
  const saveToTags = useCallback((): void => saveTag(donationCampaignKey), [donationCampaignKey, saveTag])

  return {
    hasSeenDonation: isValid ? seenDonation : true,
    saveToTags: isValid ? saveToTags : () => {},
  }
}

export const useHasSeenPaymentTypesPBIRejected = (): { seenPaymentTypesPBIRejected: boolean; saveToTags: () => void } => {
  const [tags, saveTag] = useTags()
  const PBIRejectedKey = 'seen-payment-types-experiment-b'
  const saveToTags = useCallback((): void => saveTag(PBIRejectedKey), [PBIRejectedKey, saveTag])
  const seenPaymentTypesPBIRejected = tags.includes(PBIRejectedKey)

  return {
    seenPaymentTypesPBIRejected,
    saveToTags,
  }
}

export const useShouldShowCashAppTooltip = (): {
  shouldShowCashAppAccountLinkingTooltip: boolean
  shouldShowCashAppAccountLinkingToast: boolean
  shouldOverrideCashAppPay: boolean
  saveToTags: () => void
} => {
  const [tags, saveTag] = useTags()
  const cashPayBannerKey = 'cash-app-pay-banner'
  const saveToTags = useCallback((): void => saveTag(cashPayBannerKey), [saveTag])
  const savedCards = useSavedCards()
  const method = useSelectedPaymentMethod()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')
  const isFirstTime = !tags.includes(cashPayBannerKey)
  const shouldShowCashAppAccountLinkingTooltip =
    !isArcadeDesignLanguage &&
    (isFirstTime || !cashTooltipExperiment.control) &&
    savedCards.length > 0 &&
    method != null &&
    method.type === PaymentMethodType.CASH_APP_PAY
  const shouldShowCashAppAccountLinkingToast =
    !isArcadeDesignLanguage && isFirstTime && savedCards.length === 0 && method != null && method.type === PaymentMethodType.CASH_APP_PAY

  return {
    shouldShowCashAppAccountLinkingTooltip,
    shouldShowCashAppAccountLinkingToast,
    shouldOverrideCashAppPay: isFirstTime,
    saveToTags,
  }
}

export const {
  useGetConsumerTagsWithDataQuery,
  endpoints: { getConsumerTagsWithData },
} = consumerTagsService

export const useHasSeenOpenPaymentSchedule = (): { hasSeenOpenPaymentSchedule: boolean; saveToTags: () => void } => {
  const [tags, saveTag] = useTags()
  const openPaymentScheduleKey = 'has-seen-open-payment-schedule'
  const hasSeenOpenPaymentSchedule = tags.includes(openPaymentScheduleKey)

  const saveToTags = useCallback((): void => saveTag(openPaymentScheduleKey), [openPaymentScheduleKey, saveTag])

  return {
    hasSeenOpenPaymentSchedule,
    saveToTags,
  }
}
